import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Destination } from '../../../context/AxiosContext'
import { formatInputFolderName } from '../../../helpers/regexHelper'

const DRIVE_ITEM_PARAMS = { $select: ['id', 'name', 'folder', 'parentReference'].join(',') }

/** Looking a folder up: a 404 is the normal answer, so it must not reach the global error handler. */
export const DRIVE_ITEM_CONFIG: AxiosRequestConfig = {
    baseURL: Destination.Documents,
    headers: { SuppressErrors: true },
    params: DRIVE_ITEM_PARAMS,
}

/**
 * Creating and renaming happen while the user waits for their save, so a failure there has to be
 * shown. Suppressing it left the form standing there with nothing to explain why (HAM-4357).
 */
const DRIVE_ITEM_WRITE_CONFIG: AxiosRequestConfig = {
    baseURL: Destination.Documents,
    params: DRIVE_ITEM_PARAMS,
}

/** Path of a drive item relative to the drive root, without a leading slash. */
export const getFullPath = (item: DriveItem): string => {
    const search: string = '/root:'
    const parent: string = item.parentReference.path
    const substr: string = parent.substring(parent.indexOf(search) + search.length + 1)
    return (substr.length > 0 ? substr + '/' : '') + item.name
}

/**
 * What the form has to do with the folder when it saves. The field only ever resolves the
 * intent; applyFolderPath performs it. Keeping those apart means the field never writes to
 * SharePoint while the user is still typing.
 */
export enum FolderPathAction {
    /** The stored folder already matches what is on screen. */
    None = 'NONE',
    /** Create a new folder under the parent. */
    Create = 'CREATE',
    /** Rename (and if the parent changed, move) the folder we are already pointing at. */
    Rename = 'RENAME',
    /** Point at a folder that already exists; nothing is created or renamed. */
    Adopt = 'ADOPT',
}

export type FolderPathIntent = {
    action: FolderPathAction
    /** Single, already sanitised folder name — never a path. */
    name: string
    /** Drive item the folder lives under. Undefined means the drive root. */
    parentId?: string
    /**
     * Create: undefined. None/Adopt: the folder to store. Rename: the folder being renamed,
     * which is also what gets stored once the rename succeeds.
     */
    itemId?: string
    /** Full path relative to the drive root, for display only. */
    path: string
}

/**
 * Turn free user input into a name SharePoint will accept verbatim. Only called when the user
 * leaves the field or the form submits — never while typing, because rewriting the value under
 * the cursor makes the field unusable.
 *
 * Slashes become a dash rather than being dropped, so "Acme / arbeidszaak" stays readable and
 * cannot turn into an extra folder level. The remaining rules (forbidden characters, double
 * spaces, leading/trailing whitespace and dots) come from formatInputFolderName, which the
 * documents screen already uses when renaming folders.
 */
export function sanitiseFolderName(name: string): string {
    let result = formatInputFolderName((name || '').replace(/[/\\]/g, '-'))
    // formatInputFolderName trims whitespace before it strips trailing dots, so "Acme ." still
    // comes back with a trailing space. Keep peeling until nothing changes; SharePoint would
    // otherwise silently trim the name and leave it out of sync with what we stored.
    let previous: string
    do {
        previous = result
        result = result.replace(/^[\s.]+|[\s.]+$/g, '')
    } while (result !== previous)
    return result
}

/** Graph addresses children by path, so every segment has to survive being put in a URL. */
const encodePath = (path: string): string =>
    path
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/')

/**
 * Look up a folder by name under a parent (or under the drive root when there is no parent).
 * Resolves with undefined when nothing exists at that name — that is the normal case, not an
 * error, so it must not be reported as one.
 */
export function findFolderByName(axios: AxiosInstance, name: string, parentId?: string, signal?: AbortSignal): Promise<DriveItem | undefined> {
    const url = parentId ? `/items/${parentId}:/${encodePath(name)}` : `/root:/${encodePath(name)}`

    return axios
        .get<DriveItem>(url, { ...DRIVE_ITEM_CONFIG, signal })
        .then((response: AxiosResponse<DriveItem>) => response.data)
        .catch((error: AxiosError) => {
            if (error.response?.status === 404) {
                return undefined
            }
            return Promise.reject(error)
        })
}

/**
 * Perform the intent the field resolved and return the drive item id to store. Shared by the
 * dossier and party forms so both create, rename and adopt folders in exactly the same way.
 */
export async function applyFolderPath(axios: AxiosInstance, intent: FolderPathIntent): Promise<string> {
    switch (intent.action) {
        case FolderPathAction.None:
        case FolderPathAction.Adopt:
            return intent.itemId

        case FolderPathAction.Rename: {
            const response = await axios.patch<DriveItem>(
                `/items/${intent.itemId}`,
                { name: intent.name, ...(intent.parentId ? { parentReference: { id: intent.parentId } } : {}) },
                DRIVE_ITEM_WRITE_CONFIG,
            )
            return response.data.id
        }

        case FolderPathAction.Create: {
            const response = await axios.post<DriveItem>(
                intent.parentId ? `/items/${intent.parentId}/children` : '/root/children',
                { name: intent.name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' },
                DRIVE_ITEM_WRITE_CONFIG,
            )
            return response.data.id
        }
    }
}

/** Anything the mutation forms carry a folder intent on. Frontend-only; never sent to the server. */
export type FolderIntentCarrier = {
    documentRootId?: string
    documentRootIntent?: FolderPathIntent
}

/**
 * Carry out the intent the field resolved and write the resulting id onto the mutation payload,
 * then drop the intent so it never reaches the server. Call this from a form's onSubmit, before
 * the entity itself is posted: a failure here has to block the save.
 */
export async function applyFolderIntent(axios: AxiosInstance, entity: FolderIntentCarrier): Promise<void> {
    if (!entity?.documentRootIntent) {
        return
    }
    entity.documentRootId = await applyFolderPath(axios, entity.documentRootIntent)
    delete entity.documentRootIntent
}
